import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, IconButton, Typography, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { distanceBetween } from 'geofire-common';
import { getAuth } from 'firebase/auth';
import pointsService from '../services/pointsService';
import paperSound from '../assets/audio/paper.mp3';

function NoteFormDialog(props) {
  const { open, handleClose, onProceedToPlacement, initialContent = '', initialExpiryHours = 1, userLocation, mapCenter, isMapDragged } = props;
  const [content, setContent] = useState(initialContent);
  const [expiryHours, setExpiryHours] = useState(initialExpiryHours);
  const [userPoints, setUserPoints] = useState(0);
  const paperAudioRef = useRef(null);

  // Update state when initial values change (for cancel flow)
  useEffect(() => {
    setContent(initialContent);
    setExpiryHours(initialExpiryHours);
  }, [initialContent, initialExpiryHours, open]);

  // Fetch user points when dialog opens
  useEffect(() => {
    if (open) {
      const fetchUserPoints = async () => {
        const auth = getAuth();
        if (auth.currentUser) {
          const pointsData = await pointsService.getUserPoints(auth.currentUser.uid);
          setUserPoints(pointsData?.currentPoints || 0);
        }
      };
      fetchUserPoints();
    }
  }, [open]);

  const handleContentChange = (event) => {
    setContent(event.target.value);
  };

  const incrementExpiry = () => {
    setExpiryHours(prev => prev + 1);
  };

  const decrementExpiry = () => {
    setExpiryHours(prev => Math.max(1, prev - 1));
  };

  const isRemoteDrop = () => {
    // Remote drop if map center is >100m away from user location
    if (!userLocation || !mapCenter) {
      return false;
    }
    const distance = distanceBetween(
      [userLocation.lat, userLocation.lng],
      [mapCenter.lat, mapCenter.lng]
    );
    return distance > 0.1; // 0.1km = 100m
  };

  const getPointsCost = () => {
    // Base cost: 10 points for first hour, then 5 points per additional hour
    const baseCost = 10 + ((expiryHours - 1) * 5);

    // Remote drop adds 500 points
    const remoteCost = isRemoteDrop() ? 500 : 0;

    return baseCost + remoteCost;
  };

  const handleDrop = () => {
    if (content.trim()) {
      // Play paper drop sound
      if (paperAudioRef.current) {
        paperAudioRef.current.play().catch(err => console.log('Audio play failed:', err));
      }
      onProceedToPlacement(content, expiryHours, getPointsCost());
      handleClose();
    }
  };

  const handleCancel = () => {
    setContent('');
    setExpiryHours(1);
    handleClose();
  };

  const pointsCost = getPointsCost();
  const hasEnoughPoints = userPoints >= pointsCost;

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.95)),
            repeating-linear-gradient(
              0deg,
              transparent,
              transparent 1px,
              rgba(200, 190, 170, 0.1) 1px,
              rgba(200, 190, 170, 0.1) 2px
            )
          `,
          backgroundColor: '#f9f7f4 !important',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        },
      }}
    >
      <DialogTitle sx={{ fontFamily: 'Gochi Hand, cursive', pb: 1, color: '#2c2c2c !important' }}>
        Drop a Note
      </DialogTitle>
      <DialogContent>
        {isRemoteDrop() && (
          <Alert severity="info" sx={{ mb: 2, fontFamily: 'Gochi Hand, cursive' }}>
            Remote Drop: This location is far from you. Costs +500 pts extra.
          </Alert>
        )}
        {!hasEnoughPoints && (
          <Alert severity="error" sx={{ mb: 2, fontFamily: 'Gochi Hand, cursive' }}>
            Insufficient points! You have {userPoints} pts but need {pointsCost} pts.
          </Alert>
        )}
        <TextField
          placeholder="Write your note here..."
          fullWidth
          multiline
          rows={6}
          value={content}
          onChange={handleContentChange}
          variant="standard"
          autoFocus
          sx={{
            mb: 3,
            mt: 1,
            '& .MuiInputBase-input': {
              fontFamily: 'Gochi Hand, cursive',
              fontSize: '1.1rem',
              lineHeight: 1.8,
              color: '#2c2c2c !important',
            },
            '& .MuiInputBase-input::placeholder': {
              color: '#666 !important',
              opacity: 1,
            },
            '& .MuiInput-root:before': {
              borderBottom: 'none',
            },
            '& .MuiInput-root:after': {
              borderBottom: 'none',
            },
            '& .MuiInput-root:hover:not(.Mui-disabled):before': {
              borderBottom: 'none',
            },
          }}
        />

        {/* Expiry Control - Single Line */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          pb: 2,
          borderTop: '1px dashed rgba(0,0,0,0.2)',
          pt: 2,
        }}>
          <Typography variant="body1" sx={{ fontFamily: 'Gochi Hand, cursive', color: '#2c2c2c !important', fontSize: '1rem' }}>
            Expires in
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={decrementExpiry}
              disabled={expiryHours === 1}
              size="small"
              sx={{ color: '#2c2c2c !important' }}
            >
              <RemoveIcon />
            </IconButton>
            <Typography variant="body1" sx={{ minWidth: '80px', textAlign: 'center', fontFamily: 'Gochi Hand, cursive', fontSize: '1.2rem', color: '#2c2c2c !important', fontWeight: 600 }}>
              {expiryHours}h
            </Typography>
            <IconButton
              onClick={incrementExpiry}
              size="small"
              sx={{ color: '#2c2c2c !important' }}
            >
              <AddIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCancel} sx={{ fontFamily: 'Gochi Hand, cursive', color: '#2c2c2c !important' }}>
          CANCEL
        </Button>
        <Button
          onClick={handleDrop}
          variant="contained"
          disabled={!content.trim() || !hasEnoughPoints}
          sx={{
            fontFamily: 'Gochi Hand, cursive',
            backgroundColor: '#2c2c2c !important',
            color: '#fff !important',
            '&:hover': {
              backgroundColor: '#1a1a1a !important',
            },
            '&.Mui-disabled': {
              backgroundColor: '#ccc !important',
              color: '#666 !important',
            },
          }}
        >
          DROP (-{pointsCost} pts)
        </Button>
      </DialogActions>

      {/* Hidden audio element */}
      <audio ref={paperAudioRef} src={paperSound} preload="auto" />
    </Dialog>
  );
}

export default NoteFormDialog;
